import React, { useState, useEffect } from 'react';
import { Table, Row, Col, Button, Upload, message, Spin, Empty, Input, Space, Popconfirm, Progress, Avatar } from 'antd';
import { 
  UploadOutlined, 
  DeleteOutlined, 
  VideoCameraOutlined, 
  SearchOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  PictureOutlined 
} from '@ant-design/icons';
import axios from 'axios';
import VideoModal from './VideoModal';

const MediaLibrary = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingFileName, setUploadingFileName] = useState('');
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [pageSize, setPageSize] = useState(15);

  // --- CONFIG: ADJUST CARD HEIGHT HERE ---
  const CARD_MIN_HEIGHT = '40px';
  const CARD_PADDING = '10px 20px';
  // ---------------------------------------

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/box/media/');
      setFiles(response.data);
    } catch (error) {
      console.error('Error loading media:', error);
      message.error('Error loading media library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  const handlePreview = (file) => {
    if (file.file_type === 'image') {
      setSelectedImage(file);
      setImageModalVisible(true);
    } else {
      setSelectedVideo(file);
      setVideoModalVisible(true);
    }
  };

  const handleUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    const fileType = file.type.startsWith('image/') ? 'image' : 'video';

    formData.append('file', file);
    formData.append('name', file.name);
    formData.append('file_type', fileType);
    formData.append('file_size', file.size);

    setIsUploading(true);
    setUploadingFileName(file.name);
    setUploadProgress(0);

    try {
      const response = await axios.post('/api/box/media/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      
      message.success(`File ${file.name} uploaded successfully!`);
      setIsUploading(false);
      setUploadingFileName('');
      onSuccess(response.data);
      fetchFiles();
    } catch (error) {
      setIsUploading(false);
      setUploadingFileName('');
      onError(error);
      message.error('Upload error. Max 100MB.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/box/media/${id}/`);
      message.success('File deleted');
      fetchFiles();
    } catch (error) {
      message.error('Error deleting file');
    }
  };

  const filteredFiles = files.filter(f => 
    f.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'THUMB',
      key: 'thumb',
      width: 80,
      render: (record) => (
        <div onClick={() => handlePreview(record)} style={{ cursor: 'pointer' }}>
          {record.file_type === 'image' ? (
            <Avatar shape="square" size={48} src={record.file_url || record.file} icon={<PictureOutlined />} style={{ border: '1px solid #334155' }} />
          ) : (
            <Avatar 
              shape="square" 
              size={48} 
              src={record.thumbnail ? `https://escreen.stand.ledscreen.rs/media/${record.thumbnail}` : null} 
              icon={<VideoCameraOutlined />} 
              style={{ border: '1px solid #334155', background: '#0f172a' }} 
            />
          )}
        </div>
      )
    },
    {
      title: 'FILE NAME',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <div onClick={() => handlePreview(record)} style={{ color: '#3b82f6', fontWeight: '500', cursor: 'pointer' }}>
          {text}
        </div>
      )
    },
    {
      title: 'TYPE',
      dataIndex: 'file_type',
      key: 'type',
      render: (type) => (
        <span style={{ 
          color: type === 'video' ? '#60a5fa' : '#fbbf24', 
          fontWeight: 'bold', 
          fontSize: '11px', 
          textTransform: 'uppercase' 
        }}>
          {type}
        </span>
      )
    },
    {
      title: 'DURATION',
      dataIndex: 'duration',
      key: 'duration',
      render: (text) => <span style={{ color: '#facc15' }}><ClockCircleOutlined /> {text}s</span>
    },
    {
      title: 'SIZE',
      dataIndex: 'file_size',
      key: 'size',
      render: (size) => <span style={{ color: '#94a3b8' }}>{(size / 1024 / 1024).toFixed(2)} MB</span>
    },
    {
      title: 'RESOLUTION',
      dataIndex: 'resolution',
      key: 'resolution',
      render: (text) => <span style={{ color: '#64748b' }}>{text || '-'}</span>
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 100,
      render: (record) => (
        <Popconfirm title="Delete file?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
          <Button 
            size="small" 
            icon={<DeleteOutlined />} 
            style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }}
          >Delete</Button>
        </Popconfirm>
      )
    }
  ];

  return (
    <div style={{ padding: '0px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }} align="stretch">
        <Col span={12}>
          <div style={{ background: '#1e293b', padding: CARD_PADDING, borderRadius: '8px', border: '1px solid #334155', borderBottom: '3px solid #3b82f6', minHeight: CARD_MIN_HEIGHT, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space size="middle">
              <FileTextOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>TOTAL MEDIA</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{files.length} Files</div>
              </div>
            </Space>
            <Input 
              placeholder="Search..." 
              prefix={<SearchOutlined />} 
              onChange={e => setSearchText(e.target.value)}
              style={{ width: 200, background: '#0f172a', border: '1px solid #334155', color: 'white' }}
            />
          </div>
        </Col>
        <Col span={12}>
          <div style={{ background: '#1e293b', padding: CARD_PADDING, borderRadius: '8px', border: '1px solid #334155', borderBottom: '3px solid #3b82f6', minHeight: CARD_MIN_HEIGHT, height: '100%', display: 'flex', alignItems: 'center' }}>
            <Upload customRequest={handleUpload} showUploadList={false} disabled={isUploading} style={{ width: '100%' }}>
              <Space size="middle" style={{ cursor: 'pointer' }}>
                <UploadOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
                <div>
                  <div style={{ color: '#3b82f6', fontSize: '12px', fontWeight: 'bold' }}>UPLOAD FILE</div>
                  <div style={{ color: 'white', fontSize: '14px' }}>Click to add media</div>
                </div>
              </Space>
            </Upload>
            {isUploading && (
              <div style={{ flex: 1, marginLeft: '20px' }}>
                <Progress percent={uploadProgress} size="small" strokeColor="#3b82f6" trailColor="#334155" format={(p) => <span style={{ color: 'white', fontSize: '10px' }}>{p}%</span>} />
              </div>
            )}
          </div>
        </Col>
      </Row>

      <div style={{ background: 'rgba(30, 41, 59, 0.3)', padding: '24px', borderRadius: '12px', border: '1px solid #1e293b' }}>
        <Table 
          dataSource={filteredFiles} 
          columns={columns} 
          rowKey="id" 
          loading={loading} 
          pagination={{ pageSize, showSizeChanger: true, onShowSizeChange: (_, s) => setPageSize(s) }} 
          style={{ background: 'transparent' }} 
        />
      </div>

      <VideoModal isVisible={videoModalVisible} onClose={() => setVideoModalVisible(false)} video={selectedVideo} isImage={false} />
      <VideoModal isVisible={imageModalVisible} onClose={() => setImageModalVisible(false)} video={selectedImage} isImage={true} />

      <style>{`
        .ant-table { background: transparent !important; }
        .ant-table-thead > tr > th { background: transparent !important; color: #64748b !important; border-bottom: 1px solid #1e293b !important; text-transform: uppercase; font-size: 11px; }
        .ant-table-tbody > tr > td { border-bottom: 1px solid #1e293b !important; color: white !important; }
        .ant-table-tbody > tr:hover > td { background: rgba(255,255,255,0.02) !important; }
        .ant-pagination-item a { color: #94a3b8 !important; }
        .ant-pagination-item-active { background: #3b82f6 !important; border-color: #3b82f6 !important; }
        .ant-select-selector { background: #0f172a !important; color: white !important; border: 1px solid #334155 !important; }
      `}</style>
    </div>
  );
};

export default MediaLibrary;